import React, { useEffect, useRef } from 'react';

/**
 * Sign in with Apple button.
 *
 * Uses Apple's official appleid.auth.js SDK (loaded once from index.html)
 * to configure a full-page redirect flow. Apple performs the auth on
 * appleid.apple.com and then HTTP-POSTs the id_token + user payload to
 * our backend redirect URI (form_post response mode), which is why the
 * `redirectURI` points at /api/auth/apple/callback — NOT at any
 * client-side route.
 *
 * The button is always rendered (per product decision) so users on
 * desktop Safari, Windows, and Android can benefit from Apple's privacy
 * features (Hide My Email). If the AppleID SDK failed to load (e.g.
 * offline / adblock), the button gracefully no-ops with a toast-worthy
 * console warning.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';

export default function AppleSignInButton({
  label = 'Continue with Apple',
  testId = 'apple-signin',
}) {
  const initedRef = useRef(false);

  useEffect(() => {
    if (initedRef.current) return;
    if (!APPLE_CLIENT_ID) return;
    if (typeof window === 'undefined' || !window.AppleID) return;

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: `${BACKEND_URL}/api/auth/apple/callback`,
        state: window.crypto?.randomUUID?.() || String(Date.now()),
        usePopup: false,
      });
      initedRef.current = true;
    } catch (e) {
      console.warn('AppleID.auth.init failed:', e);
    }
  }, []);

  const handleClick = () => {
    if (!APPLE_CLIENT_ID) {
      console.warn('Sign in with Apple is not configured (REACT_APP_APPLE_CLIENT_ID missing).');
      return;
    }
    if (!window.AppleID) {
      console.warn('Apple ID SDK not loaded.');
      return;
    }
    try {
      window.AppleID.auth.signIn();
    } catch (e) {
      console.warn('AppleID.auth.signIn failed:', e);
    }
  };

  // If the SDK isn't loaded or clientId isn't configured, we still
  // render the button so the layout stays stable across environments
  // (staging vs prod). Click will no-op safely.
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-md border border-black bg-black hover:bg-neutral-900 text-white font-medium transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 170 170" aria-hidden="true" fill="currentColor">
        <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.37.13-9.13-1.9-14.27-6.08-3.57-2.88-7.39-7.53-11.46-13.93-4.58-7.2-8.51-15.64-11.79-25.31-3.28-9.66-4.92-19.19-4.92-28.59 0-13.75 3.53-24.93 10.58-33.52 7.04-8.59 15.86-12.93 26.46-13.02 5.37 0 11.03 1.57 17 4.71 5.97 3.14 10.25 4.71 12.83 4.71 2.35 0 6.71-1.62 13.08-4.88 6.38-3.25 11.83-4.76 16.33-4.52 11.08.45 19.8 4.54 26.17 12.28-8.83 5.38-13.13 12.78-12.91 22.22.23 7.84 3.03 14.31 8.42 19.4 5.39 5.1 11.88 7.86 19.49 8.28-.9 2.91-1.91 5.76-3.03 8.52zM119.22 29.17c0-6.72 2.36-13.08 7.08-18.07 4.73-5 10.45-7.79 17.18-8.4a25.43 25.43 0 0 1-5.74 17.63c-4.8 5.61-10.63 8.61-17.47 9-.67-.78-1.05-1.16-1.05-.16z" />
      </svg>
      <span>{label}</span>
    </button>
  );
}
